/**
 * useScrollLock — freeze page scrolling without touching `overflow`.
 *
 * The site keeps the viewport scrollbar permanently visible
 * (`html { overflow-y: scroll }` in global.css) so the page never
 * shifts sideways when content height changes. Scroll locking must
 * preserve that invariant: toggling `body.style.overflow` (the old
 * approach) hides/shows the viewport scrollbar and shifts every
 * centered element the moment the lock releases.
 *
 * Instead, this hook pins the window to its current offset and
 * cancels the gestures that would move it. Scrollable elements
 * inside overlays (comment drawers, dialogs) keep working: window
 * `scroll` events don't fire for element scrolling, and wheel/touch
 * gestures are only cancelled when there is no scrollable ancestor
 * between the event target and the page.
 */
import { useEffect } from 'react';

const hasScrollableAncestor = (target: EventTarget | null): boolean => {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body && el !== document.documentElement) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
};

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const x = window.scrollX;
    const y = window.scrollY;

    // The snap must not animate through `scroll-behavior: smooth`
    const html = document.documentElement;
    const previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    const cancelGesture = (e: Event) => {
      if (hasScrollableAncestor(e.target)) return;
      e.preventDefault();
    };
    // Catches whatever the gesture handlers can't (keyboard,
    // programmatic scrolls): pin the window back in place before
    // the next paint.
    const snap = () => window.scrollTo(x, y);

    window.addEventListener('wheel', cancelGesture, { passive: false });
    window.addEventListener('touchmove', cancelGesture, { passive: false });
    window.addEventListener('scroll', snap);

    return () => {
      window.removeEventListener('wheel', cancelGesture);
      window.removeEventListener('touchmove', cancelGesture);
      window.removeEventListener('scroll', snap);
      html.style.scrollBehavior = previousBehavior;
    };
  }, [active]);
}

export default useScrollLock;
